import {createAsyncThunk, createSlice, PayloadAction} from '@reduxjs/toolkit';
import axios from 'axios';
import {each} from 'lodash';
import URL_CONST from '../../config/url';
import {
  DatasetMetadataBinaries,
  DatasetMetadataCategories,
  DatasetMetadataMethodItem,
  DatasetMetadataResponse,
  DatasetMetadataSplit,
  SplitDistributionCount,
  SplitDistributionTotal,
} from '../../types/dataService/DatasetMetadata';
import {PagingParams} from '../../types/dataService/PagingParams';
import {
  CategoryType,
  DataPrepareType,
} from '../../types/dataset/DatasetGetAnnotationsResponse';
import {getHeaders} from './index';

export const DATASET_METADATA = 'dataset/metadata';

const DEFAULT_PAGE_REQUEST = {
  page: 0,
  size: 10,
};
export const MAX_METADATA_INFO = {
  page: 0,
  size: 1000,
};

export type DatasetMetadataMethodInfo = {
  id?: string;
  name?: string | null;
  description?: string | null;
  method?: DataPrepareType;
};

export type DatasetMetadataState = {
  isBusy: boolean;
  isBusyInfo: boolean;
  isBusyCategory: boolean;
  isBusySplit: boolean;
  isBusyBinary: boolean;
  isBusyUpdate: boolean;
  isBusyDelete: boolean;

  // Info
  info: DatasetMetadataMethodInfo | null;
  // Categories
  categories?: DatasetMetadataCategories | null;
  // Binaries (image/videos)
  binaries: DatasetMetadataBinaries | null;

  splits: DatasetMetadataSplit | null;
};

const initialState: DatasetMetadataState = {
  isBusy: false,
  isBusyCategory: false,
  isBusySplit: false,
  isBusyBinary: false,
  isBusyInfo: false,
  isBusyUpdate: false,
  isBusyDelete: false,

  info: null,
  categories: null,
  binaries: null,
  splits: null,
};

// https://studio.dev5.nc.blaize.com/dataset-service/swagger-ui/index.html#/Metadata/deleteMetadata

export const deleteMetadata = async (args: {
  datasetId: string;
  metadataIds?: string;
  batchIds?: string;
}) => {
  const {data} = await axios.delete<any>(URL_CONST.DSS_DATASET_METADATA, {
    headers: await getHeaders(),
    params: {
      ...args,
    },
  });
  return data.body;
};

// Delete generic Metadata
export const deleteDatasetMetadata = createAsyncThunk(
  `${DATASET_METADATA}/delete`,
  (args: {datasetId: string; metadataIds: string}) => deleteMetadata(args)
);
// Delete Dataset Categories
export const deleteDatasetCategoryMetadata = createAsyncThunk(
  `${DATASET_METADATA}/delete/category`,
  (args: {datasetId: string; metadataIds: string}) => deleteMetadata(args)
);

// Delete by BatchID
export const deleteDatasetMetadataByBatchId = createAsyncThunk(
  `${DATASET_METADATA}/deleteByBatchId`,
  (args: {datasetId: string; batchIds: string}) => deleteMetadata(args)
);
// Create and Update are the same
// https://studio.dev5.nc.blaize.com/dataset-service/swagger-ui/index.html#/Metadata/addMetadata
export const updateDatasetMetadata = createAsyncThunk(
  `${DATASET_METADATA}/update`,
  async (args: {flow?: string; datasetId: string; payload: any[]}) => {
    const {datasetId, payload} = args;
    const {data} = await axios.post<DatasetMetadataResponse>(
      URL_CONST.DSS_DATASET_METADATA,
      payload,
      {
        headers: await getHeaders(),
        params: {
          datasetId,
        },
      }
    );
    return data.body;
  }
);

// Get Dataset Metadata
// https://studio.dev5.nc.blaize.com/dataset-service/swagger-ui/index.html#/Metadata/queryMetadata
export const getMetadata = async (args: {
  datasetId: string;
  version?: number;
  pageable: PagingParams;
  rsql?: string;
  fields?: string;
}) => {
  const {pageable, ...rest} = args;
  const {data} = await axios.get<DatasetMetadataResponse>(
    URL_CONST.DSS_DATASET_METADATA,
    {
      headers: await getHeaders(),
      params: {
        ...rest,
        ...pageable,
      },
    }
  );
  return data.body;
};

// Get Dataset Categories
export const getDatasetCategoryMetadata = createAsyncThunk(
  `${DATASET_METADATA}/get/category`,
  (args: {datasetId: string}) =>
    getMetadata({
      ...args,
      pageable: DEFAULT_PAGE_REQUEST,
      rsql: 'type=="annotation/categories_list"',
      fields: 'id,categories',
    })
);

// Get Dataset Metadata
export const getDatasetInfoMetadata = createAsyncThunk(
  `${DATASET_METADATA}/get/method`,
  (args: {datasetId: string; version?: number}) =>
    getMetadata({
      ...args,
      pageable: DEFAULT_PAGE_REQUEST,
      rsql: 'type=="annotation/common_info"',
      fields: 'id,name,description,custom',
    })
);

export const getDatasetMetadataSplits = createAsyncThunk(
  `${DATASET_METADATA}`,
  (args: {datasetId: string; type?: string; version?: number}) => {
    const {type = 'statistics/labels_by_splits'} = args;
    return getMetadata({
      ...args,
      pageable: MAX_METADATA_INFO,

      rsql: `type=="${type}"`,
    });
  }
);

export const getDatasetBinaryMetadata = createAsyncThunk(
  `${DATASET_METADATA}/get/binary`,
  (args: {datasetId: string; version: number; pageable: PagingParams}) =>
    getMetadata({
      ...args,
      rsql: 'type=in=(binary/image,binary/video)',
      fields: 'id,created,mediaType,originalPath,size,updated',
    })
);

const saveResponse: any = {
  info: (state: DatasetMetadataState, res: DatasetMetadataMethodItem[]) => {
    const node = res[0];
    state.info = {
      ...node,
    };
  },
  categories: (state: DatasetMetadataState, res: DatasetMetadataCategories[]) => {
    const node = res[0];
    const formattedCategories: CategoryType[] = [];
    if (node) {
      const list = node.categories;
      for (const key in list) {
        formattedCategories.push({
          id: Number(key),
          name: list[key],
        });
      }
    }
    state.categories = {
      ...node,
      formattedCategories,
    } as DatasetMetadataCategories;
  },
  splits: (state: DatasetMetadataState, res: DatasetMetadataSplit[]) => {
    const node = res[0];
    const labelTotals: SplitDistributionCount = {};
    const distributionTotals: SplitDistributionTotal = {};
    each(node.distribution, (splitDist, key) => {
      let total = 0;
      each(splitDist, (labelCount, labelKey) => {
        const key = Number(labelKey);
        labelTotals[key] = (labelTotals[key] || 0) + labelCount;
        total += labelCount;
      });
      distributionTotals[String(key)] = total;
    });
    state.splits = {
      // copy response
      ...node,
      // totals on each split
      distributionTotals,
      // totals on each label
      labelTotals,
    };
  },
};

const busyKeys = [
  'isBusyInfo',
  'isBusyCategory',
  'isBusySplit',
  'isBusyBinary',
  'isBusyUpdate',
  'isBusyDelete',
];
type BusyKeysType =
  | 'isBusyInfo'
  | 'isBusyCategory'
  | 'isBusySplit'
  | 'isBusyBinary'
  | 'isBusyUpdate'
  | 'isBusyDelete';
const setIsBusy = (
  state: DatasetMetadataState,
  busyKey: BusyKeysType,
  busy: boolean = true
) => {
  state[busyKey] = busy;
  const otherKeys = busyKeys.filter(el => el !== busyKey) as BusyKeysType[];
  state.isBusy = busy || otherKeys.some(el => state[el]);
};

const datasetSlice = createSlice({
  name: 'datasetMetadata',
  initialState,
  reducers: {
    reset() {
      return initialState;
    },
    updateDatasetMetadataCategoriesLocal(
      state,
      action: PayloadAction<DatasetMetadataCategories | undefined>
    ) {
      state.categories = action.payload ? action.payload : null;
    },
  },
  extraReducers: builder => {
    builder
      .addCase(updateDatasetMetadata.pending, (state: DatasetMetadataState) =>
        setIsBusy(state, 'isBusyUpdate')
      )
      .addCase(updateDatasetMetadata.fulfilled, (state, action) => {
        action.meta.arg.flow && saveResponse[action.meta.arg.flow](state, action.payload);
        setIsBusy(state, 'isBusyUpdate', false);
      })

      .addCase(deleteDatasetMetadata.pending, (state: DatasetMetadataState) =>
        setIsBusy(state, 'isBusyDelete')
      )
      .addCase(deleteDatasetMetadata.fulfilled, (state, action) => {
        setIsBusy(state, 'isBusyDelete', false);
      })

      .addCase(getDatasetInfoMetadata.pending, (state: DatasetMetadataState) =>
        setIsBusy(state, 'isBusyInfo')
      )
      .addCase(getDatasetInfoMetadata.fulfilled, (state, action) => {
        saveResponse.info(state, action.payload.list);
        setIsBusy(state, 'isBusyInfo', false);
      })

      .addCase(getDatasetCategoryMetadata.pending, (state: DatasetMetadataState) =>
        setIsBusy(state, 'isBusyCategory')
      )
      .addCase(getDatasetCategoryMetadata.fulfilled, (state, action) => {
        saveResponse.categories(state, action.payload.list);
        setIsBusy(state, 'isBusyCategory', false);
      })

      .addCase(getDatasetMetadataSplits.pending, (state: DatasetMetadataState) =>
        setIsBusy(state, 'isBusySplit')
      )
      .addCase(getDatasetMetadataSplits.fulfilled, (state, action) => {
        saveResponse.splits(state, action.payload.list);
        setIsBusy(state, 'isBusySplit', false);
      });
  },
});

export const {
  actions: datasetMetadataActions,
  reducer: datasetMetadataReducer,
} = datasetSlice;
